import React, { useEffect } from 'react';
import { Tabs } from 'antd';
import { useLocation, useHistory } from 'react-router-dom';
import { RoutableTabsProps } from '@src/types';



/**
 * RoutableTabs - A generic tab component that synchronizes tab state with URL parameters
 * 
 * Features:
 * - URL synchronization: Tab state persists in query parameters (e.g., ?tab=tabKey)
 * - Direct navigation: Users can navigate directly to specific tabs via URL
 * - Bookmark support: Each tab can be bookmarked with its unique URL
 * - React Router integration: Works seamlessly with React Router v5
 * - Customizable: Supports all Ant Design Tabs properties
 * 
 * @param tabs - Array of tab configurations with key, tab label, and content
 * @param defaultActiveKey - Default tab to show when no URL parameter exists
 * @param queryParam - URL query parameter name (defaults to 'tab')
 * @param onTabChange - Optional callback when tab changes
 * @param ...props - Standard Ant Design Tabs props (tabPosition, size, type, etc.)
 */
const RoutableTabs: React.FC<RoutableTabsProps> = (
    {tabs, defaultActiveKey, tabPlacement = 'top', size = 'default', type = 'line', className, style, queryParam = 'tab', onTabChange, tabBarExtraContent,}) => {
    
    // React Router hooks for URL management
    const location = useLocation();
    const history = useHistory();

    /**
     * Extracts the active tab key from the current URL query parameters
     * Falls back to defaultActiveKey or first tab if URL param is invalid/missing
     */
    const getActiveTabFromUrl = (): string => {
        const params = new URLSearchParams(location.search);
        const tabFromUrl = params.get(queryParam);

        // Validate that the tab key exists in our tabs array
        if (tabFromUrl && tabs.some(tab => tab.key === tabFromUrl)) {
            return tabFromUrl;
        }

        // Fallback to default or first tab
        return defaultActiveKey || tabs[0]?.key || '';
    };

    // Get the currently active tab from URL
    const activeTab = getActiveTabFromUrl();

    /**
     * Handles tab changes by updating the URL query parameters
     * This ensures tab state is preserved in the URL for bookmarking/sharing
     */
    const handleTabChange = (key: string) => {
        // Parse existing query parameters
        const params = new URLSearchParams(location.search);
        
        // Update the tab parameter
        params.set(queryParam, key);

        // Build new URL preserving pathname and hash
        const newUrl = `${location.pathname}?${params.toString()}${location.hash}`;
        
        // Navigate to new URL (adds to browser history)
        history.push(newUrl);

        // Call optional callback
        if (onTabChange) {
            onTabChange(key);
        }
    };

    /**
     * Effect to initialize URL with default tab if no tab parameter exists
     * Uses history.replace to avoid adding unnecessary history entries
     */
    useEffect(() => {
        if (!getActiveTabFromUrl() && defaultActiveKey) {
            const params = new URLSearchParams(location.search);
            params.set(queryParam, defaultActiveKey);
            const newUrl = `${location.pathname}?${params.toString()}${location.hash}`;
            // Use replace instead of push to avoid cluttering browser history
            history.replace(newUrl);
        }
        }, []);

    return (
        <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            tabPlacement={tabPlacement}
            // @ts-ignore - Ant Design types may not match exactly
            size={size}
            type={type}
            className={className}
            style={style}
            tabBarExtraContent={tabBarExtraContent}
            items={tabs.map((tab) => ({
                key: tab.key,
                label: tab.tab,
                disabled: tab.disabled,
                children: tab.content,
            }))}
        />
    );
};

export default RoutableTabs;